import { createSlice } from "@reduxjs/toolkit";

import { TASK_ACTIVITY_WINDOW } from "./constants";
import { fetchEnvironmentTasksThunk } from "./thunks/fetchEnvironmentTasks.thunk";
import { runEnvironmentTaskThunk } from "./thunks/runEnvironmentTask.thunk";
import { createEnvironmentTasksEntry, getEnvironmentTasksKey } from "./utils";

import type { EnvironmentTasksSliceState } from "./types";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Activity } from "Api/activity/types";
import type { ApiErrorPayload } from "src/common/utils/apiErrorHandling";

export const initialState: EnvironmentTasksSliceState = {
  byEnvironment: {}
};

const environmentTasksSlice = createSlice({
  name: "environmentTasks",
  initialState,
  reducers: {
    /**
     * Merge a streamed `environment.task` activity into an already-loaded
     * environment entry, keeping the card's status + last-run live as runs
     * start and settle (including ones triggered outside the console).
     *
     * Replaces the activity with the same id in place — so an
     * `in_progress -> complete/failed` transition flips the row — or prepends a
     * brand-new run, then caps the list to the windowed size. No-ops when the
     * environment has no entry yet: we only hold definitions for environments
     * the card has loaded, and a ghost entry spun up from a stream event alone
     * would have nothing to join against.
     */
    taskActivityReceived: (
      state,
      action: PayloadAction<{ key: string; activity: Activity }>
    ) => {
      const { key, activity } = action.payload;
      const entry = state.byEnvironment[key];
      if (!entry) return;

      const index = entry.activities.findIndex(a => a.id === activity.id);
      if (index === -1) {
        entry.activities.unshift(activity);
      } else {
        entry.activities[index] = activity;
      }

      if (entry.activities.length > TASK_ACTIVITY_WINDOW) {
        entry.activities.splice(TASK_ACTIVITY_WINDOW);
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchEnvironmentTasksThunk.pending, (state, action) => {
        const key = getEnvironmentTasksKey(action.meta.arg);
        const entry = state.byEnvironment[key] ?? createEnvironmentTasksEntry();
        entry.isLoading = true;
        entry.error = null;
        entry.isNonRetryableError = false;
        state.byEnvironment[key] = entry;
      })
      .addCase(fetchEnvironmentTasksThunk.fulfilled, (state, action) => {
        const { key, definitions, activities } = action.payload;
        const entry = state.byEnvironment[key] ?? createEnvironmentTasksEntry();
        entry.definitions = definitions;
        entry.activities = activities;
        entry.isLoading = false;
        entry.hasLoaded = true;
        entry.error = null;
        entry.isNonRetryableError = false;
        state.byEnvironment[key] = entry;
      })
      .addCase(fetchEnvironmentTasksThunk.rejected, (state, action) => {
        const key = getEnvironmentTasksKey(action.meta.arg);
        const payload = action.payload as ApiErrorPayload | undefined;
        const entry = state.byEnvironment[key] ?? createEnvironmentTasksEntry();
        entry.isLoading = false;
        entry.error = payload?.error ?? "Failed to load task containers.";
        entry.isNonRetryableError = payload?.isNonRetryable ?? false;
        state.byEnvironment[key] = entry;
      })
      .addCase(runEnvironmentTaskThunk.pending, (state, action) => {
        const key = getEnvironmentTasksKey(action.meta.arg);
        const entry = state.byEnvironment[key] ?? createEnvironmentTasksEntry();
        entry.runningByTask[action.meta.arg.taskName] = true;
        state.byEnvironment[key] = entry;
      })
      .addCase(runEnvironmentTaskThunk.fulfilled, (state, action) => {
        const { key, taskName } = action.payload;
        const entry = state.byEnvironment[key];
        if (entry) entry.runningByTask[taskName] = false;
      })
      .addCase(runEnvironmentTaskThunk.rejected, (state, action) => {
        const key = getEnvironmentTasksKey(action.meta.arg);
        const entry = state.byEnvironment[key];
        if (entry) entry.runningByTask[action.meta.arg.taskName] = false;
      });
  }
});

export const { taskActivityReceived } = environmentTasksSlice.actions;

export const environmentTasksReducer = environmentTasksSlice.reducer;
